import { type Locale } from '@syncanix/shared';

import { type CatalogGroup } from './catalog-groups';
import { createTranslator, type MarketingCatalog, type Translator } from './translator';

/**
 * Piece-cached, group-aware catalog loader (ADR-0195).
 *
 * Every `<locale> × <group>` catalog piece is its own lazy chunk (emitted by
 * `vite-plugin-catalog-groups.ts` from the six SOT JSONs), so a route
 * downloads only the strings it renders: the landing page needs `core`
 * (~10% of the old monolithic catalog) instead of the whole site — `docs.*`
 * alone was 56% and shipped to every visitor, blocking hydration.
 *
 * Loading rules:
 *   - `core` is always included — it carries the shared chrome + any new
 *     namespace `groupOfKey` doesn't recognize.
 *   - The EN baseline pieces and the active locale's override pieces are
 *     fetched **in parallel** (the pre-split loader awaited EN before even
 *     starting the locale fetch — two serial round-trips on non-EN pages).
 *   - Per-piece failure degrades to `{}`: a failed override falls back to the
 *     EN baseline; an unfetchable EN piece is as broken as an unfetchable
 *     entry chunk and degrades to key-echo rather than throwing out of the
 *     mount path.
 *
 * Server-side (prerender + tests) the synchronous mirror in
 * `load-catalog.server.ts` statically imports the six FULL catalogs — that
 * graph never reaches the browser bundle.
 */

export type PieceLoader = () => Promise<MarketingCatalog>;
export type PieceLoaders = Readonly<Record<Locale, Readonly<Record<CatalogGroup, PieceLoader>>>>;

/* The 18 dynamic imports are written out literally so Vite's static analysis
   emits one lazy chunk per piece (named `catalog-<locale>-<group>-<hash>.js`
   via the chunkFileNames hook in vite.config.ts). */
const VIRTUAL_LOADERS: PieceLoaders = {
  en: {
    core: () => import('virtual:syncanix-catalog/en/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/en/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/en/docs').then((m) => m.default),
  },
  es: {
    core: () => import('virtual:syncanix-catalog/es/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/es/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/es/docs').then((m) => m.default),
  },
  fr: {
    core: () => import('virtual:syncanix-catalog/fr/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/fr/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/fr/docs').then((m) => m.default),
  },
  de: {
    core: () => import('virtual:syncanix-catalog/de/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/de/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/de/docs').then((m) => m.default),
  },
  ar: {
    core: () => import('virtual:syncanix-catalog/ar/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/ar/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/ar/docs').then((m) => m.default),
  },
  he: {
    core: () => import('virtual:syncanix-catalog/he/core').then((m) => m.default),
    pages: () => import('virtual:syncanix-catalog/he/pages').then((m) => m.default),
    docs: () => import('virtual:syncanix-catalog/he/docs').then((m) => m.default),
  },
};

export interface CatalogLoader {
  /** Resolve the merged catalog for `locale` covering `groups` (+ `core`). */
  loadCatalog(locale: Locale, groups: readonly CatalogGroup[]): Promise<MarketingCatalog>;
  /** Resolve the {@link Translator} for `locale` covering `groups` (+ `core`). */
  loadTranslator(locale: Locale, groups: readonly CatalogGroup[]): Promise<Translator>;
}

/** Build a loader over injectable piece loaders (the seam the tests use). */
export function createCatalogLoader(loaders: PieceLoaders): CatalogLoader {
  const pieceCache = new Map<string, Promise<MarketingCatalog>>();

  function piece(locale: Locale, group: CatalogGroup): Promise<MarketingCatalog> {
    const key = `${locale}/${group}`;
    const hit = pieceCache.get(key);
    if (hit !== undefined) return hit;
    const loading = loaders[locale][group]().catch(() => ({}));
    pieceCache.set(key, loading);
    return loading;
  }

  async function loadCatalog(
    locale: Locale,
    groups: readonly CatalogGroup[],
  ): Promise<MarketingCatalog> {
    const wanted = [...new Set<CatalogGroup>(['core', ...groups])];
    const enPieces = wanted.map((group) => piece('en', group));
    const overridePieces = locale === 'en' ? [] : wanted.map((group) => piece(locale, group));
    // EN baseline + overrides all in flight together; merge keeps EN first so
    // every override wins over its baseline, and group pieces are disjoint.
    const resolved = await Promise.all([...enPieces, ...overridePieces]);
    return Object.assign({}, ...resolved) as MarketingCatalog;
  }

  async function loadTranslator(
    locale: Locale,
    groups: readonly CatalogGroup[],
  ): Promise<Translator> {
    return createTranslator(await loadCatalog(locale, groups));
  }

  return { loadCatalog, loadTranslator };
}

const defaultLoader = createCatalogLoader(VIRTUAL_LOADERS);

export const loadCatalog: CatalogLoader['loadCatalog'] = (locale, groups) =>
  defaultLoader.loadCatalog(locale, groups);
export const loadTranslator: CatalogLoader['loadTranslator'] = (locale, groups) =>
  defaultLoader.loadTranslator(locale, groups);
